"use client";

import type {
  Attribute,
  Availability,
  Combination,
  Shop,
} from "@workspace/ui/types";
import type { Dispatch } from "react";
import React, { createContext, useContext, useMemo, useReducer } from "react";

import { ShopAvailabilityAPI } from "@/const/endPoint";
import axiosInstance from "@/utils/api/fetchData/axiosInstance";

import { snakeToCamelCaseObject } from "../../utils";

export enum ShopAvailabilityActionType {
  START_SHOP_AVAILABILITIES = "START_SHOP_AVAILABILITIES",
  ERROR_SHOP_AVAILABILITIES = "ERROR_SHOP_AVAILABILITIES",
  SUCCESS_SHOP_AVAILABILITIES = "SUCCESS_SHOP_AVAILABILITIES",

  SELECT_COLOR = "SELECT_COLOR",
  SELECT_SIZE = "SELECT_SIZE",

  // Search
  START_SEARCH = "START_SEARCH",
  ERROR_SEARCH = "ERROR_SEARCH",
  SUCCESS_SEARCH = "SUCCESS_SEARCH",
}

export type ShopAvailabilityState = {
  combinations: Combination[];
  attributes: Attribute[];
  selectedColor: Attribute | null;
  selectedSize: Attribute | null;
  selectedReference: string | null;
  loadingShopAvailabilities: boolean;
  errorShopAvailabilities: string | null;
  // Search
  searchedAvailability: Availability[];
  searchedShops: Shop[];
  searchAvailabilityLoading: boolean;
  searchAvailabilityError: string | null;
};

const ShopAvailabilityStateContext = createContext<
  ShopAvailabilityState | undefined
>(undefined);

type ShopAvailabilityAction =
  | { type: ShopAvailabilityActionType.START_SHOP_AVAILABILITIES }
  | {
      type: ShopAvailabilityActionType.ERROR_SHOP_AVAILABILITIES;
      errorShopAvailabilities: string;
    }
  | {
      type: ShopAvailabilityActionType.SUCCESS_SHOP_AVAILABILITIES;
      combinations: Combination[];
      attributes: Attribute[];
    }
  | { type: ShopAvailabilityActionType.SELECT_COLOR; color: Attribute | null }
  | { type: ShopAvailabilityActionType.SELECT_SIZE; size: Attribute | null }
  // Search
  | { type: ShopAvailabilityActionType.START_SEARCH }
  | {
      type: ShopAvailabilityActionType.ERROR_SEARCH;
      searchAvailabilityError: string;
    }
  | {
      type: ShopAvailabilityActionType.SUCCESS_SEARCH;
      searchedAvailability: Availability[];
      searchedShops: Shop[];
    };

export type ShopAvailabilityDispatch = Dispatch<ShopAvailabilityAction>;

const ShopAvailabilityDispatchContext = createContext<
  ShopAvailabilityDispatch | undefined
>(undefined);

const initialShopAvailabilityState: ShopAvailabilityState = {
  combinations: [],
  attributes: [],
  selectedColor: null,
  selectedSize: null,
  selectedReference: null,
  loadingShopAvailabilities: false,
  errorShopAvailabilities: null,
  // Search
  searchedAvailability: [],
  searchedShops: [],
  searchAvailabilityLoading: false,
  searchAvailabilityError: null,
};

function shopAvailabilityReducer(
  state: ShopAvailabilityState,
  action: ShopAvailabilityAction,
): ShopAvailabilityState {
  switch (action.type) {
    case ShopAvailabilityActionType.START_SHOP_AVAILABILITIES:
      return {
        ...state,
        loadingShopAvailabilities: true,
      };
    case ShopAvailabilityActionType.ERROR_SHOP_AVAILABILITIES:
      return {
        ...state,
        errorShopAvailabilities: action.errorShopAvailabilities,
        loadingShopAvailabilities: false,
      };
    case ShopAvailabilityActionType.SUCCESS_SHOP_AVAILABILITIES:
      return {
        ...state,
        combinations: action.combinations,
        attributes: action.attributes,
        loadingShopAvailabilities: false,
      };

    case ShopAvailabilityActionType.SELECT_COLOR:
      return {
        ...state,
        selectedColor: action.color,
      };

    case ShopAvailabilityActionType.SELECT_SIZE:
      if (state.selectedColor && action.size) {
        const combination = state.combinations.find((comb) => {
          const attributes = Object.values(comb.attributes);
          if (state.selectedColor !== null && action.size !== null) {
            return (
              attributes.includes(state.selectedColor.attributeName) &&
              attributes.includes(action.size.attributeName)
            );
          }
          return null;
        });

        return {
          ...state,
          selectedSize: action.size,
          selectedReference: combination?.reference || null,
        };
      }

      return {
        ...state,
        selectedSize: action.size,
      };

    // Search
    case ShopAvailabilityActionType.START_SEARCH:
      return {
        ...state,
        searchAvailabilityLoading: true,
      };
    case ShopAvailabilityActionType.ERROR_SEARCH:
      return {
        ...state,
        searchAvailabilityError: action.searchAvailabilityError,
        searchAvailabilityLoading: false,
      };
    case ShopAvailabilityActionType.SUCCESS_SEARCH:
      return {
        ...state,
        searchedAvailability: action.searchedAvailability,
        searchedShops: action.searchedShops,
        searchAvailabilityLoading: false,
      };

    default:
      throw new Error("Unhandled action");
  }
}

export function ShopAvailabilityProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(
    shopAvailabilityReducer,
    initialShopAvailabilityState,
  );

  return (
    <ShopAvailabilityDispatchContext.Provider value={dispatch}>
      <ShopAvailabilityStateContext.Provider
        value={useMemo(() => state, [state])}
      >
        {children}
      </ShopAvailabilityStateContext.Provider>
    </ShopAvailabilityDispatchContext.Provider>
  );
}

export function useShopAvailabilityState() {
  const state = useContext(ShopAvailabilityStateContext);
  if (!state) throw new Error("ShopAvailabilityProvider not found");
  return state;
}

export function useShopAvailabilityDispatch() {
  const dispatch = useContext(ShopAvailabilityDispatchContext);
  if (!dispatch) throw new Error("ShopAvailabilityProvider not found");
  return dispatch;
}

export const searchAvailability = async (
  shopQuery: string,
  reference: string,
  dispatch: ShopAvailabilityDispatch,
) => {
  dispatch({ type: ShopAvailabilityActionType.START_SEARCH });

  await axiosInstance
    .get(
      `/rest${ShopAvailabilityAPI}?reference=${reference}&shop_query=${shopQuery}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
    .then((res) => {
      if (res.data.psdata) {
        dispatch({
          type: ShopAvailabilityActionType.SUCCESS_SEARCH,
          searchedAvailability: snakeToCamelCaseObject(
            res.data.psdata.availability,
          ),
          searchedShops: snakeToCamelCaseObject(res.data.psdata.shops),
        });
      } else {
        dispatch({
          type: ShopAvailabilityActionType.ERROR_SEARCH,
          searchAvailabilityError: "Nie znaleziono dostępności",
        });
      }
    })
    .catch((error) => {
      dispatch({
        type: ShopAvailabilityActionType.ERROR_SEARCH,
        searchAvailabilityError: error.message,
      });
    });
};

export const useShopAvailability = () => {
  const state = useShopAvailabilityState();
  const dispatch = useShopAvailabilityDispatch();

  const selectColor = (color: Attribute | null) => {
    dispatch({ type: ShopAvailabilityActionType.SELECT_COLOR, color });
  };
  const selectSize = (size: Attribute | null) => {
    dispatch({ type: ShopAvailabilityActionType.SELECT_SIZE, size });
  };

  // Search
  const searchAvailabilityFunc = (locationName: string, reference: string) =>
    searchAvailability(locationName, reference, dispatch);

  const clearSearch = () => {
    dispatch({
      type: ShopAvailabilityActionType.SUCCESS_SEARCH,
      searchedAvailability: [],
      searchedShops: [],
    });
  };

  const setShopAvailability = (
    combinations: Combination[],
    attributes: Attribute[],
  ) => {
    dispatch({
      type: ShopAvailabilityActionType.SUCCESS_SHOP_AVAILABILITIES,
      combinations,
      attributes,
    });
  };

  return {
    state,
    selectColor,
    selectSize,
    // Search
    searchAvailabilityFunc,
    clearSearch,
    setShopAvailability,
  };
};
